import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core'
import { SUPPORTED_STATES } from '@ftr/contracts/shared/State'
import { ProductionStatus } from '@ftr/contracts/type/court/ProductionStatus'
import {
  FoundationBadgeColors,
  FoundationBadgeConfig,
  FoundationalBadgeComponent,
  IconComponent,
  TooltipAlignment,
  TooltipModule,
} from '@ftr/foundation'
import { CourtSystemSelectorItem } from '~app/features/court-system-selector/court-system-selector-item/court-system-selector-item.interface'

export type CourtSystemSelectorBadgeTypes =
  | ProductionStatus.Sandbox
  | ProductionStatus.PreProduction
  | ProductionStatus.Archived
  | 'isPublished'

const BADGE_CONFIG: Record<CourtSystemSelectorBadgeTypes, FoundationBadgeConfig> = {
  [ProductionStatus.Sandbox]: {
    color: FoundationBadgeColors.Grey20,
    text: 'Sandbox',
    icon: 'Sandbox',
  },
  [ProductionStatus.PreProduction]: {
    color: FoundationBadgeColors.Primary,
    text: 'Pilot',
    icon: 'PaperPlane',
  },
  [ProductionStatus.Archived]: {
    color: FoundationBadgeColors.Lemon,
    text: 'Archived',
    icon: 'Archive',
  },
  ['isPublished']: {
    color: FoundationBadgeColors.Apple,
    text: 'Published',
    icon: 'CheckCircle',
  },
}

@Component({
  selector: 'ftr-court-system-selector-item',
  templateUrl: './court-system-selector-item.component.html',
  styleUrls: ['./court-system-selector-item.component.css'],
  standalone: true,
  imports: [IconComponent, FoundationalBadgeComponent, TooltipModule],
})
export class CourtSystemSelectorItemComponent implements OnInit {
  @Input() courtSystem: CourtSystemSelectorItem
  @Input() skipBadges: CourtSystemSelectorBadgeTypes[] = []
  @Input() disabled = false // Does not respond to a click when true
  @Output() onSelect = new EventEmitter<CourtSystemSelectorItem>()

  stateName: string
  badgeConfigs: FoundationBadgeConfig[] = []

  readonly tooltipAlignment = TooltipAlignment
  readonly externalPortalTooltip = 'You will be redirected to an external transcript ordering portal'

  ngOnInit(): void {
    this.setStateName()
    this.setBadgeConfigs()
  }

  handleOnClick(): void {
    this.onSelect.emit(this.courtSystem)
  }

  handleOnEnter(): void {
    this.onSelect.emit(this.courtSystem)
  }

  setFocusToPrevious(event: Event): void {
    const { previousCards } = CourtSystemSelectorItemComponent.getCardsAndCurrentPlacement(event.target)
    const focusCard = previousCards.at(previousCards.length - 1)

    if (focusCard) {
      focusCard.focus()
    }
  }

  setFocusToNext(event: Event): void {
    const { nextCards } = CourtSystemSelectorItemComponent.getCardsAndCurrentPlacement(event.target)
    const focusCard = nextCards.at(0)

    if (focusCard) {
      focusCard.focus()
    }
  }

  setFocusToAbove = CourtSystemSelectorItemComponent.swallowKeyPress((event: Event) => {
    const { currentCard, previousCards } = CourtSystemSelectorItemComponent.getCardsAndCurrentPlacement(event.target)
    const focusCard = CourtSystemSelectorItemComponent.findCardAbove(currentCard, previousCards.reverse())

    if (focusCard) {
      focusCard.focus()
    }
  })

  setFocusToBelow = CourtSystemSelectorItemComponent.swallowKeyPress((event: Event) => {
    const { currentCard, nextCards } = CourtSystemSelectorItemComponent.getCardsAndCurrentPlacement(event.target)
    const focusCard = CourtSystemSelectorItemComponent.findCardBelow(currentCard, nextCards)

    if (focusCard) {
      focusCard.focus()
    }
  })

  private setStateName(): void {
    this.stateName =
      SUPPORTED_STATES.find(
        s => s.countryCode === this.courtSystem.countryCode && s.code === this.courtSystem.stateCode,
      )?.name || ''
  }

  private setBadgeConfigs(): void {
    const isPublishedBadgeConfig = this.courtSystem.isPublished ? BADGE_CONFIG.isPublished : null
    if (isPublishedBadgeConfig && !this.skipBadges.includes('isPublished')) {
      this.badgeConfigs.push(isPublishedBadgeConfig)
    }

    const productionStatusBadgeConfig =
      this.courtSystem.productionStatus !== ProductionStatus.Production
        ? BADGE_CONFIG[this.courtSystem.productionStatus]
        : null
    if (
      productionStatusBadgeConfig &&
      !this.skipBadges.includes(this.courtSystem.productionStatus as CourtSystemSelectorBadgeTypes)
    ) {
      this.badgeConfigs.push(productionStatusBadgeConfig)
    }
  }

  private static swallowKeyPress(fn: (event: Event) => void) {
    return (event: Event) => {
      event.preventDefault()
      event.stopPropagation()

      fn(event)
      return false
    }
  }

  private static getCardsAndCurrentPlacement(currentCard: EventTarget | null): {
    previousCards: HTMLElement[]
    currentCard: HTMLElement
    nextCards: HTMLElement[]
  } {
    const cards = Array.from(document.querySelectorAll<HTMLElement>('.court-system-selector-item'))
    const currentCardIndex = cards.findIndex((card: HTMLElement) => card === currentCard)

    return {
      previousCards: cards.slice(0, currentCardIndex),
      currentCard: currentCard as HTMLElement,
      nextCards: cards.slice(currentCardIndex + 1),
    }
  }

  private static findCardAbove(targetCard: HTMLElement, searchingCards: HTMLElement[]): HTMLElement | null {
    const targetCardBounds = targetCard.getBoundingClientRect()

    if (searchingCards.length) {
      const [nextCard, ...nextSearch] = searchingCards
      if (nextCard) {
        const nextCardBounds = nextCard.getBoundingClientRect()

        // Search up, and to the left
        if (nextCardBounds.top < targetCardBounds.top && nextCardBounds.left <= targetCardBounds.left) {
          return nextCard
        }
      }
      return this.findCardAbove(targetCard, nextSearch)
    }

    return null
  }

  private static findCardBelow(targetCard: HTMLElement, searchingCards: HTMLElement[]): HTMLElement | null {
    const targetCardBounds = targetCard.getBoundingClientRect()

    if (searchingCards.length) {
      const [nextCard, ...nextSearch] = searchingCards
      if (nextCard) {
        const nextCardBounds = nextCard.getBoundingClientRect()

        // Make sure its lower than the current target
        if (nextCardBounds.top > targetCardBounds.top) {
          // if its in the same column, then select it
          if (nextCardBounds.left >= targetCardBounds.left) {
            return nextCard
          }

          // And if there are no more cards on this row, select it
          if (nextSearch.at(0)?.getBoundingClientRect().top !== nextCardBounds.top) {
            return nextCard
          }
        }
      }

      return this.findCardBelow(targetCard, nextSearch)
    }

    return null
  }
}
